import { createToast } from "./toast-message";

// Class for creating tabs
// =============================================================================
export class Tabs {
	constructor(element) {
		// Setup the element
		this.element = element;

		// Navigation items
		this.tabs = element.querySelectorAll("[data-tab]");

		// Content items
		this.panels = element.querySelectorAll("[data-tab-panel]");

		// Initialize the tabs
		this.init();

		// Set active tab
		this.activateTab(this.getActiveTab());
	}

	init() {
		// Initialize the navigation
		this.tabs.forEach((tab) => {
			if (tab === this.getActiveTab()) {
				tab.classList.add("active");
			}

			tab.addEventListener("click", this.handleTabButtonClick.bind(this, tab));
		});

		// Initialize the panels
		this.panels.forEach((panel) => {
			panel.setAttribute("aria-hidden", "true");
		});
	}

	activateTab(tab) {
		if (tab) {
			// Deactivate all tabs
			this.deactivateTabs();

			// Activate the tab
			tab.classList.add("active");
			tab.classList.remove("disabled");
			tab.setAttribute("aria-selected", "true");

			// Activate the panel
			this.activatePanel(tab);
		}
	}

	deactivateTabs() {
		this.tabs.forEach((tab) => {
			tab.classList.remove("active");
			tab.setAttribute("aria-selected", "false");

			// Deactivate the panel
			this.decactivePanel(tab);
		});
	}

	getActiveTab() {
		if (this.element.querySelector("[data-tab].active")) {
			return this.element.querySelector("[data-tab].active");
		} else {
			return this.element.querySelector("[data-tab]");
		}
	}

	getActiveTabPanel() {
		if (this.element.querySelector("[data-tab-panel].active")) {
			return this.element.querySelector("[data-tab-panel].active");
		} else {
			return this.element.querySelector("[data-tab-panel]");
		}
	}

	getTabPanel(tab) {
		return this.element.querySelector(`[data-tab-panel="${tab.getAttribute("data-tab")}"]`);
	}

	activatePanel(tab) {
		this.panels.forEach((panel) => {
			if (panel.getAttribute("data-tab-panel") === tab.getAttribute("data-tab")) {
				// This panel is the one we want to activate
				panel.classList.add("active");
				panel.setAttribute("aria-hidden", "false");
				panel.style.display = "block";

				// Fire a custom event
				this.triggerEvent("tabActivated", {
					tab: tab,
					panel: panel,
				});
			} else {
				// Otherwise deactivate it
				this.decactivePanel(panel);
			}
		});
	}

	decactivePanel(tab) {
		this.panels.forEach((panel) => {
			if (panel.getAttribute("data-tab-panel") === tab.getAttribute("data-tab")) {
				panel.classList.remove("active");
				panel.setAttribute("aria-hidden", "true");
				panel.style.display = "none";
			}
		});
	}

	triggerEvent(name, data) {
		const event = new CustomEvent(name, {
			bubbles: false,
			detail: data,
		});

		this.element.dispatchEvent(event);
	}

	handleTabButtonClick(tab, e) {
		e.preventDefault();

		this.scrollToButton(tab);
		this.activateTab(tab);
	}

	scrollToButton(el) {
		el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
	}
}

export class FormTabs extends Tabs {
	constructor(element) {
		super(element);

		this.completedSteps = null;

		this.initPrevNextButtons();
	}

	initPrevNextButtons() {
		this.element.querySelectorAll("[data-btn-next]").forEach((button) => {
			button.addEventListener("click", this.handleNextClick.bind(this, button));
		});

		this.element.querySelectorAll("[data-btn-previous]").forEach((button) => {
			button.addEventListener("click", this.handlePreviousClick.bind(this, button));
		});
	}

	getFormSteps() {
		return this.tabs;
	}

	getCompletedSteps() {
		return this.completedSteps;
	}

	setCompletedSteps(completedSteps) {
		this.completedSteps = completedSteps;
	}

	updateCompletedSteps(step) {
		let completedSteps = this.getCompletedSteps();

		if (completedSteps === null) completedSteps = [];

		// Add the step to the completed steps if it doesn't already exist
		if (!completedSteps.includes(step)) {
			completedSteps.push(step);
		}

		this.setCompletedSteps(completedSteps);
	}

	removeCompletedStep(step) {
		let completedSteps = this.getCompletedSteps();

		if (completedSteps === null) return;

		// Remove the step from the completed steps if it exists
		if (completedSteps.includes(step)) {
			completedSteps.splice(completedSteps.indexOf(step), 1);
		}

		this.setCompletedSteps(completedSteps);
	}

	validateTab() {
		// Validate the form elements for this tab
		const activeTab = this.getActiveTabPanel();
		let invalidItems = 0;

		// Get the form elements
		const formElements = activeTab.querySelectorAll("input, select, textarea, [data-validation]");
		formElements.forEach((element) => {
			element.classList.remove("error");
			void element.offsetWidth;
		});

		// Loop the form elements and validate the required ones
		formElements.forEach((element) => {
			if (element.required) {
				// This element is required
				if (element.value === "") {
					// The element is empty
					element.classList.add("error");

					// Increment the invalid items
					invalidItems++;
				} else {
					if (element.type === "email" && element.dataset.validateCompare) {
						const elementToCompare = activeTab.querySelector("#" + element.dataset.validateCompare);

						if (element.value !== elementToCompare.value) {
							element.classList.add("error");
							elementToCompare.classList.add("error");

							invalidItems += 2;
						} else {
							element.classList.remove("error");
							elementToCompare.classList.remove("error");
						}
					} else {
						// The element is not empty
						element.classList.remove("error");
					}
				}
			}
		});

		return invalidItems;
	}

	isCurrentTabValid() {
		// Validate the current tab
		const invalidItems = this.validateTab();

		// If there are no invalid items, the tab is valid
		if (invalidItems === 0) {
			return true;
		} else {
			return false;
		}
	}

	handleTabButtonClick(nextStep, e) {
		e.preventDefault();

		const currentStep = this.getActiveTab();

		if (currentStep.nextElementSibling === nextStep) {
			this.handleStepValidation(currentStep, nextStep);
		} else {
			this.moveToTab(nextStep);
		}
	}

	setIncompleteTab(tab) {
		tab.classList.remove("completed");
		this.getTabPanel(tab).classList.remove("completed");

		// Remove the completed step
		this.removeCompletedStep(tab);

		// Fire a toast to say the form is incomplete
		let errorMessage = tab.dataset.errorMessage ? tab.dataset.errorMessage : "Please complete the form before continuing";
		const toast = createToast(errorMessage, "error");
	}

	setCompletedTab(tab) {
		tab.classList.add("completed");
		this.getTabPanel(tab).classList.add("completed");

		// Update the completed steps
		this.updateCompletedSteps(tab);
	}

	handleStepValidation(currentStep, goToStep) {
		// Validate the tab
		this.validateTab();

		if (this.isCurrentTabValid()) {
			// Set the current step as completed
			this.setCompletedTab(currentStep);

			// Unlock the button for the next step
			this.unlockNextTab();

			// Go to tab
			this.moveToTab(goToStep);
		} else {
			this.setIncompleteTab(currentStep);
		}
	}

	moveToTab(goToStep) {
		// Activate the next step's tab
		this.activateTab(goToStep);

		// Scroll to the next step's button
		this.scrollToButton(goToStep);
	}

	handleNextClick(button, e) {
		e.preventDefault();

		// Get the current step
		const currentStep = this.getActiveTab();

		// Work out the next step
		const nextStep = currentStep.nextElementSibling;

		this.handleStepValidation(currentStep, nextStep);
	}

	handlePreviousClick(button, e) {
		// Get the current step
		const currentStep = this.getActiveTab();

		// Work out the next step
		const previousStep = currentStep.previousElementSibling;

		this.moveToTab(previousStep);
	}

	unlockNextTab() {
		// Get the current step
		const currentStep = this.getActiveTab();

		// Work out the next step
		const nextStep = currentStep.nextElementSibling;
		nextStep.classList.remove("disabled");
	}
}

(function () {
	const tabs = document.querySelectorAll("[data-tabs]").forEach((tab) => {
		if (tab.dataset.tabs === "form") {
			return;
		} else {
			return new Tabs(tab);
		}
	});

	document.getElementById("login-modal-content")?.addEventListener("tabActivated", (e) => {
		// Change the modal title to the tab title
		document.getElementById("login-modal-title").innerText = e.detail.tab.dataset.title;
	});
})();
